import { useState, useEffect, useRef, useCallback } from 'react';

interface ProcessingStatus {
  isProcessing: boolean;
  progress: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  statusText: string;
}

export function useProcessingStatus(
  versionId: string | null,
  onComplete?: () => void
): ProcessingStatus {
  const [status, setStatus] = useState<ProcessingStatus>({
    isProcessing: false,
    progress: 0,
    status: 'completed',
    statusText: ''
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const getStatusText = useCallback((status: string, progress: number) => {
    switch (status) {
      case "pending":
        return "Preparing to process...";
      case "processing":
        return `Converting pages to images... ${progress}%`;
      case "completed":
        return "Processing complete!";
      case "failed":
        return "Processing failed";
      default:
        return "Processing...";
    }
  }, []);

  useEffect(() => {
    if (!versionId) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setStatus({
        isProcessing: false,
        progress: 0,
        status: 'completed',
        statusText: ''
      });
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(`/api/documents/versions/${versionId}/status`);
        if (!response.ok) {
          // Non-OK response — log but keep polling; transient server errors are common
          console.warn('Processing status poll returned non-OK:', response.status);
          return;
        }
        
        const statusData = await response.json();
        const isProcessing = statusData.status !== 'completed' && statusData.status !== 'failed';
        
        const newProgress = statusData.progress || 0;
        const newStatusText = getStatusText(statusData.status, newProgress);
        setStatus(prev => {
          if (
            prev.isProcessing === isProcessing &&
            prev.progress === newProgress &&
            prev.status === statusData.status &&
            prev.statusText === newStatusText
          ) return prev;
          return { isProcessing, progress: newProgress, status: statusData.status, statusText: newStatusText };
        });
        
        // Stop polling when terminal state reached
        if (!isProcessing && pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          // Notify parent so it can refresh document data
          onCompleteRef.current?.();
        }
      } catch (error) {
        // Network error — log but keep polling so we recover when server comes back
        console.error('Failed to poll processing status (will retry):', error);
      }
    };

    // Initial poll immediately
    pollStatus();

    // Then poll every 2 seconds
    pollIntervalRef.current = setInterval(pollStatus, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [versionId, getStatusText]);

  return status;
}
